import { getConflicts, getSelectedEntity } from 'ducks/core';
import { useDispatch, useSelector } from 'react-redux';
import { animated, useTransition } from 'react-spring';
import styled from 'styled-components';
import { IncludedShelfComponent, RotatableComponent } from '@inter-ikea-storageone/range';
import { ComponentIds } from '@inter-ikea-core/core';
import { ProductInfoComponent, TVComponent } from '@inter-ikea-storageone/range';
import { CustomizedSlidingDoorComponent } from '@inter-ikea-storageone/range-pax';
import { useCore } from 'contexts/core';
import { setShowMoveButtonsMenu } from 'ducks/moveButtonsMenu';
import { getMarketProducts } from 'ducks/products';
import { e2e } from 'utils/queryUtils';
import Customise from './actions/Customise';
import Delete from './actions/Delete';
import Duplicate from './actions/Duplicate';
import Info from './actions/Info';
import Move from './actions/Move';
import Rotate from './actions/Rotate';
import NonDeletableComponent from '@inter-ikea-storageone/range/src/core-api/components/NonDeletableComponent';
import NonDuplicatableComponent from '@inter-ikea-storageone/range/src/core-api/components/NonDuplicatableComponent';
import RoomConfig from './actions/RoomConfig';
import { isSpaceEnabled } from 'utils/integration';
import { useServiceSettings } from 'contexts/serviceSettings';

const Container = styled(animated.div)`
  position: absolute;
  z-index: 2;
  bottom: 0;
  left: calc(50% - (${props => props.theme.layout.sidebarWidth} / 2));
  transform: translate(-50%, 0%);
  margin: 0 auto 1.5rem;
  padding: 0 ${({ theme }) => theme.space.xs};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ColoredContainer = styled(Container)`
  background: ${props => props.theme.colors.feedback.selected};
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 8px 8px rgba(0, 0, 0, 0.04);
  border-radius: ${({ theme }) => theme.space.xl};
`;

type Props = {
  slideInOutProps?: any;
};

const ContextMenu = ({ slideInOutProps }: Props) => {
  const { enableKeyboardNavigation } = useServiceSettings().dexfSettings.general;
  const conflicts = useSelector(getConflicts);
  const coreApi = useCore();
  const hasConflicts = conflicts.length > 0;
  const selectedProduct = useSelector(getSelectedEntity);
  const products = useSelector(getMarketProducts);
  const dispatch = useDispatch();
  const selectedEntity = selectedProduct && coreApi.entities.find(selectedProduct.entityId);
  let enableInfo = !!selectedEntity;
  const enableRotate = selectedEntity?.hasComponent(RotatableComponent.id);
  const nonDeletable = selectedEntity?.hasComponent(NonDeletableComponent.id);
  let nonDuplicateable = selectedEntity?.hasComponent(NonDuplicatableComponent.id);

  if (selectedEntity?.hasComponent(IncludedShelfComponent.id)) {
    // should not be possible to duplicate or get info for included shelves in corner solution if market doesn't sell thoose as separate products
    const size = coreApi.entities.getEntityComSize(selectedEntity);
    const type = coreApi.entities.getProductType(selectedEntity);
    const design = selectedEntity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
      ?.design;
    enableInfo = !!products.find(
      p =>
        p.design === design &&
        p.type === type &&
        p.communicatedDimensions.width === size?.width &&
        p.communicatedDimensions.depth === size.depth
    );
    if (!enableInfo) {
      nonDuplicateable = true;
    }
  } else {
    enableInfo =
      !selectedEntity?.hasComponent(CustomizedSlidingDoorComponent.id) &&
      !selectedEntity?.hasComponent(TVComponent.id);
  }
  const handleSkipButton = () => {
    const disableVerticalButtons = !selectedEntity?.hasComponent(ComponentIds.FRAME_COMPONENT_ID);

    const elementToFocus = document.getElementById(
      !disableVerticalButtons ? 'move-right-button' : 'move-up-button'
    );

    if (elementToFocus !== null) {
      const buttons = elementToFocus.querySelectorAll('button:not([disabled])');
      if (buttons[0] !== undefined) {
        (buttons[0] as HTMLElement).focus();
      }
    }
  };

  const enableMoveButton = false;

  const onMoveClicked = () => {
    dispatch(setShowMoveButtonsMenu(true));
    setTimeout(() => {
      handleSkipButton();
    }, 100);
  };

  const enableSpacePlanning = isSpaceEnabled();
  const enableContextMenuRoomConfig = !enableSpacePlanning;

  const transitions = useTransition(
    selectedEntity && !hasConflicts
      ? 'contextMenu'
      : enableContextMenuRoomConfig
      ? 'roomConfig'
      : null,
    {
      from: { opacity: 0, transform: 'translateY(100%)' },
      enter: { opacity: 1, transform: 'translateY(0%)' },
      leave: { opacity: 0, transform: 'translateY(100%)' },
      config: { tension: 330, mass: 1, friction: 25 }
    }
  );

  return (
    <>
      {transitions((style, item) => {
        if (item === 'contextMenu' && selectedEntity) {
          return (
            <ColoredContainer
              data-testid="contextual-menu"
              style={
                e2e()
                  ? undefined
                  : {
                      ...style,
                      left:
                        slideInOutProps &&
                        slideInOutProps.left.to((left: any) => `calc(50% - ${left}rem)`),
                      transform: style.transform.to(
                        t => `${t} translateX(-50%)` // Combine animation and centering logic
                      )
                    }
              }
            >
              {enableKeyboardNavigation && <Customise size="small" />}
              {!nonDeletable && <Delete selectedEntity={selectedEntity} size="small" />}
              {enableRotate && <Rotate selectedEntity={selectedEntity} size="small" />}
              {!nonDuplicateable && <Duplicate selectedEntity={selectedEntity} size="small" />}
              {enableMoveButton && <Move onClick={onMoveClicked} size="small" />}
              {enableInfo && <Info selectedEntity={selectedEntity} size="small" />}
            </ColoredContainer>
          );
        }
        if (item === 'roomConfig') {
          return (
            <Container
              data-testid="contextual-roomConfig-menu"
              style={
                e2e()
                  ? undefined
                  : {
                      ...style,
                      left:
                        slideInOutProps &&
                        slideInOutProps.left.to((left: any) => `calc(50% - ${left}rem)`),
                      transform: style.transform.to(
                        t => `${t} translateX(-50%)` // Combine animation and centering logic
                      )
                    }
              }
            >
              <RoomConfig />
            </Container>
          );
        }
        return null;
      })}
    </>
  );
};

export default ContextMenu;
